import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Award,
  BadgeCheck,
  BarChart3,
  Briefcase,
  Building2,
  ChevronLeft,
  ChevronRight,
  Filter,
  Loader2,
  Lock,
  MapPin,
  RefreshCw,
  Rocket,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  X,
  Zap,
} from 'lucide-react';
import {
  AnalyticsSection,
  AVAILABILITY_OPTIONS,
  CandidateDataRow,
  DataRow,
  FacetCount,
  FacetGroup,
  RecruiterDataRow,
  ShareAudience,
  TalentAnalytics,
  TalentStats,
} from '../../Admin/Types/CandidateDataType';
import {
  trackShareEvent,
  viewSharedDirectory,
  viewSharedDirectoryAnalytics,
} from '../../Admin/Services/CandidateDataService';

interface PublicFilters {
  q: string;
  city: string;
  role: string;
  availabilityStatus: string;
  roleLevel: string;
  domain: string;
  expertise: string;
  minExperience: string;
  maxExperience: string;
  fromDate: string;
  toDate: string;
  /** Generic analytics drill-down: attribute + value of the clicked distribution bar. */
  field: string;
  value: string;
}

const EMPTY: PublicFilters = {
  q: '',
  city: '',
  role: '',
  availabilityStatus: '',
  roleLevel: '',
  domain: '',
  expertise: '',
  minExperience: '',
  maxExperience: '',
  fromDate: '',
  toDate: '',
  field: '',
  value: '',
};

const PAGE_SIZE = 20;

/** Order the "Talent Insights" categories are shown in; unknown categories fall to the end. */
const CATEGORY_ORDER = [
  'Experience',
  'Who they are',
  'Availability',
  'Expertise',
  'Leadership & scope',
  'Track record',
  'Compensation',
  'Quality & trust',
  'Activity',
  'Recruiters',
  'Companies',
];

/** Bucket labels that represent missing data; they are shown but cannot drill into a filter. */
const UNKNOWN_BUCKETS = new Set(['Not specified', 'Not scored', 'Not set', 'Unknown', '']);

/** Rotating accent per category so the Insights wall stays colourful and scannable. */
const CATEGORY_COLORS = [
  'text-indigo-600',
  'text-violet-600',
  'text-emerald-600',
  'text-fuchsia-600',
  'text-sky-600',
  'text-amber-600',
  'text-rose-600',
  'text-teal-600',
  'text-cyan-600',
  'text-orange-600',
  'text-lime-600',
];

/** Matching left-border accents for the Insights section cards. */
const CATEGORY_BORDERS = [
  'border-l-indigo-400',
  'border-l-violet-400',
  'border-l-emerald-400',
  'border-l-fuchsia-400',
  'border-l-sky-400',
  'border-l-amber-400',
  'border-l-rose-400',
  'border-l-teal-400',
  'border-l-cyan-400',
  'border-l-orange-400',
  'border-l-lime-400',
];

/** Rotating dot colour for the facet cards' headers. */
const FACET_DOTS = [
  'bg-indigo-500',
  'bg-violet-500',
  'bg-emerald-500',
  'bg-fuchsia-500',
  'bg-sky-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-teal-500',
];

const parseExperienceBucket = (label: string): { min: string; max: string } => {
  const years = label.match(/\d+/g)?.map(Number) ?? [];
  if (label.includes('+')) return { min: String(years[0] ?? 10), max: '' };
  if (years.length >= 2) return { min: String(years[0]), max: String(years[1]) };
  return { min: '', max: '' };
};

const maskName = (name: string): string => {
  const first = (name || '').trim().split(' ')[0] || 'Confidential';
  return `${first.charAt(0)}${'•'.repeat(Math.max(3, first.length - 1))}`;
};

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const FILTER_LABELS: Record<keyof PublicFilters, string> = {
  q: 'Search',
  city: 'City',
  role: 'Role',
  availabilityStatus: 'Availability',
  roleLevel: 'Role level',
  domain: 'Domain',
  expertise: 'Expertise',
  minExperience: 'Min exp',
  maxExperience: 'Max exp',
  fromDate: 'From',
  toDate: 'To',
  field: 'Field',
  value: 'Value',
};

/**
 * Public landing page for a shared directory ({@code /share/:token}).
 *
 * <p>No auth: the token is the only credential. The page mirrors the admin Candidates Data console
 * — talent-density stats, the full "Talent Insights" catalogue, facet counts and the complete
 * filter set — so the recipient sees the exact same numbers the admin sees. Identities stay masked
 * (no emails) and every refinement is reported back as a tracked event.</p>
 */
const SharedDirectoryPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [audience, setAudience] = useState<ShareAudience>('CANDIDATE');
  const [title, setTitle] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('ACTIVE');
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [stats, setStats] = useState<TalentStats | null>(null);
  const [analytics, setAnalytics] = useState<TalentAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [filters, setFilters] = useState<PublicFilters>(EMPTY);
  const [applied, setApplied] = useState<PublicFilters>(EMPTY);
  const [page, setPage] = useState(0);

  const [rows, setRows] = useState<DataRow[]>([]);
  const [facets, setFacets] = useState<FacetGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [verifying, setVerifying] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCandidate = audience === 'CANDIDATE';

  const buildParams = useCallback(
    () => ({
      audience,
      page,
      size: PAGE_SIZE,
      q: applied.q || undefined,
      city: applied.city || undefined,
      role: applied.role || undefined,
      availabilityStatus: applied.availabilityStatus || undefined,
      roleLevel: applied.roleLevel || undefined,
      domain: applied.domain || undefined,
      expertise: applied.expertise || undefined,
      minExperience: applied.minExperience ? Number(applied.minExperience) : null,
      maxExperience: applied.maxExperience ? Number(applied.maxExperience) : null,
      fromDate: applied.fromDate || undefined,
      toDate: applied.toDate || undefined,
      field: applied.field || undefined,
      value: applied.value || undefined,
    }),
    [audience, page, applied]
  );

  const load = useCallback(async () => {
    if (!token) {
      setError('This link is not available');
      setVerifying(false);
      return;
    }
    setLoading(true);
    setAnalyticsLoading(true);
    const params = buildParams();
    const [res, analyticsRes] = await Promise.all([
      viewSharedDirectory(token, params),
      viewSharedDirectoryAnalytics(token, params),
    ]);
    if (res.success && res.data) {
      setError(null);
      setAudience(res.data.audience);
      setTitle(res.data.title);
      setNote(res.data.note);
      setStatus(res.data.status);
      setExpiresAt(res.data.expiresAt);
      setStats(res.data.stats ?? null);
      setRows(res.data.results.content ?? []);
      setFacets(res.data.results.facets ?? []);
      setTotal(res.data.results.totalElements ?? 0);
      setTotalPages(res.data.results.totalPages ?? 0);
    } else {
      setError(res.message || 'This shared link is not available');
    }
    setAnalytics(analyticsRes.success && analyticsRes.data ? analyticsRes.data : null);
    setVerifying(false);
    setLoading(false);
    setAnalyticsLoading(false);
  }, [token, buildParams]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = (next: PublicFilters) => {
    setApplied(next);
    setPage(0);
    if (token) {
      trackShareEvent(token, { eventType: 'FILTERS_APPLIED', filtersJson: JSON.stringify(next) });
    }
  };

  const clearFilter = (key: keyof PublicFilters) => {
    applyFilters({ ...applied, [key]: EMPTY[key] });
  };

  const applyFacet = (group: FacetGroup, value: string) => {
    const next = { ...applied, field: '', value: '' };
    if (group.key === 'city') next.city = value;
    else if (group.key === 'role') next.role = value;
    else if (group.key === 'availabilityStatus') next.availabilityStatus = value;
    else if (group.key === 'experience') {
      const bucket = parseExperienceBucket(value);
      next.minExperience = bucket.min;
      next.maxExperience = bucket.max;
    } else {
      return;
    }
    applyFilters(next);
  };

  /**
   * Drill into a "Talent Insights" bar. Experience buckets map onto the min/max filter pair; every
   * other clickable section uses the generic field/value filter — both are merged into the share's
   * stored filters server-side.
   */
  const applyAnalyticsFilter = (section: AnalyticsSection, value: string) => {
    if (section.key === 'experience') {
      const bucket = parseExperienceBucket(value);
      if (bucket.min === '' && bucket.max === '') {
        return;
      }
      applyFilters({
        ...applied,
        field: '',
        value: '',
        minExperience: bucket.min,
        maxExperience: bucket.max,
      });
      return;
    }
    if (!section.filterField) {
      return;
    }
    applyFilters({
      ...applied,
      city: '',
      role: '',
      availabilityStatus: '',
      roleLevel: '',
      domain: '',
      expertise: '',
      minExperience: '',
      maxExperience: '',
      field: section.filterField as string,
      value,
    });
  };

  const gotoPage = (next: number) => {
    setPage(next);
    if (token) {
      trackShareEvent(token, { eventType: 'RESULT_VIEWED', metadata: `page=${next + 1}` });
    }
  };

  const submitOnboard = () => {
    if (token) {
      trackShareEvent(token, { eventType: 'ONBOARD_CLICKED', metadata: 'register-cta' });
    }
    navigate('/early-access', { state: { userType: 'recruiter', source: 'share', token } });
  };

  const activeChips = useMemo(
    () =>
      (Object.keys(applied) as (keyof PublicFilters)[])
        .filter((k) => applied[k] !== '')
        .map((k) => ({ key: k, label: `${FILTER_LABELS[k]}: ${applied[k]}` })),
    [applied]
  );

  const hoursLeft = useMemo(() => {
    if (!expiresAt) return null;
    const diff = new Date(expiresAt).getTime() - Date.now();
    return diff > 0 ? Math.round(diff / 3_600_000) : 0;
  }, [expiresAt]);

  const seniorPct = stats && stats.total > 0 ? Math.round((stats.seniorCandidates / stats.total) * 100) : 0;

  if (verifying) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-900 text-white">
        <Loader2 className="animate-spin" size={30} />
        <p className="text-sm font-semibold tracking-wide text-white/70">Verifying secure link…</p>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
        <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-500">
            <Lock size={26} />
          </span>
          <h1 className="mt-5 text-lg font-black text-slate-800">This link is not available</h1>
          <p className="mt-2 text-sm font-medium text-slate-500">
            {error ?? 'The shared list was revoked, expired, or never existed.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-16 text-slate-800">
      {/* HERO */}
      <div className="bg-gradient-to-br from-indigo-700 via-violet-700 to-fuchsia-700 px-4 pb-20 pt-8 md:px-8">
        <div className="mx-auto max-w-[1600px]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 text-white">
                <Sparkles size={18} />
              </span>
              <span className="text-lg font-black tracking-tight text-white">Nexgent</span>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/40 bg-emerald-400/15 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-emerald-100">
              <ShieldCheck size={13} /> Verified link
              {hoursLeft != null && <span className="opacity-80">· expires in {hoursLeft}h</span>}
            </span>
          </div>

          <div className="mt-8 max-w-3xl">
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-white/60">
              {isCandidate ? 'Talent density report' : 'Recruiter network report'}
            </p>
            <h1 className="mt-2 text-3xl font-black leading-tight text-white md:text-4xl">
              {title ?? (isCandidate ? 'Senior talent, ready to hire' : 'Active recruiters on Nexgent')}
            </h1>
            {note && <p className="mt-2 max-w-2xl text-sm font-medium text-white/80">{note}</p>}
          </div>
        </div>
      </div>

      <div className="mx-auto -mt-12 max-w-[1600px] px-4 md:px-8">
        {/* FILTERS — full-width horizontal bar */}
        <div className="rounded-2xl border border-t-4 border-slate-200 border-t-indigo-400 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-400">
            <Filter size={14} className="text-indigo-500" /> Filters
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            <label className="col-span-2 block">
              <span className="mb-1 block text-[11px] font-bold text-slate-500">Search</span>
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3">
                <Search size={14} className="text-slate-400" />
                <input
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyFilters(filters);
                  }}
                  placeholder={isCandidate ? 'Name, role, company' : 'Recruiter or designation'}
                  className="w-full bg-transparent py-2 text-sm outline-none"
                />
              </div>
            </label>

            {isCandidate && (
              <>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-bold text-slate-500">City</span>
                  <input
                    value={filters.city}
                    onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-bold text-slate-500">Role</span>
                  <input
                    value={filters.role}
                    onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-bold text-slate-500">Role level</span>
                  <input
                    value={filters.roleLevel}
                    onChange={(e) => setFilters({ ...filters, roleLevel: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-bold text-slate-500">Availability</span>
                  <select
                    value={filters.availabilityStatus}
                    onChange={(e) => setFilters({ ...filters, availabilityStatus: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none"
                  >
                    <option value="">Any</option>
                    {AVAILABILITY_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-bold text-slate-500">Min exp</span>
                  <input
                    type="number"
                    value={filters.minExperience}
                    onChange={(e) => setFilters({ ...filters, minExperience: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-bold text-slate-500">Max exp</span>
                  <input
                    type="number"
                    value={filters.maxExperience}
                    onChange={(e) => setFilters({ ...filters, maxExperience: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none"
                  />
                </label>
              </>
            )}

            <label className="block">
              <span className="mb-1 block text-[11px] font-bold text-slate-500">From</span>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-2 text-xs outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold text-slate-500">To</span>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-2 text-xs outline-none"
              />
            </label>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            <button
              onClick={() => applyFilters(filters)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 py-2 text-xs font-black uppercase tracking-wider text-white transition hover:bg-slate-800"
            >
              <RefreshCw size={14} /> Apply
            </button>
            <button
              onClick={() => {
                setFilters(EMPTY);
                applyFilters(EMPTY);
              }}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-500 transition hover:bg-slate-50"
            >
              Reset
            </button>

            {activeChips.length > 0 && (
              <div className="ml-auto flex flex-wrap items-center gap-2">
                {activeChips.map((chip) => (
                  <button
                    key={chip.key}
                    onClick={() => clearFilter(chip.key)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-[11px] font-bold text-indigo-600 transition hover:bg-indigo-100"
                  >
                    {chip.label}
                    <X size={12} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* FACET COUNTS — full-width row */}
        {facets.length > 0 && (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {facets.map((group, i) => (
              <div key={group.key} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-400">
                  <span className={`h-2 w-2 rounded-full ${FACET_DOTS[i % FACET_DOTS.length]}`} />
                  {group.label}
                </div>
                {group.values.length === 0 ? (
                  <p className="text-[11px] font-semibold text-slate-400">No values</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {group.values.map((v) => (
                      <button
                        key={v.value}
                        onClick={() => applyFacet(group, v.value)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-indigo-300 hover:text-indigo-600"
                      >
                        {v.value}
                        <span className="rounded-md bg-slate-100 px-1.5 text-[10px] font-black text-slate-500">
                          {v.count}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Results — full width */}
        <main className="mt-5 space-y-4">
            {/* Talent density */}
            <section className="rounded-2xl border border-t-4 border-slate-200 border-t-emerald-400 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-400">
                <TrendingUp size={14} className="text-emerald-500" /> Talent density
                <span className="ml-auto text-[10px] font-bold normal-case text-slate-400">
                  scoped to current filters
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatTile
                  icon={<Users size={15} />}
                  label={isCandidate ? 'In pool' : 'Recruiters'}
                  value={stats.total}
                  accent="indigo"
                />
                {isCandidate ? (
                  <>
                    <StatTile
                      icon={<Award size={15} />}
                      label="10+ years"
                      value={stats.seniorCandidates}
                      accent="emerald"
                    />
                    <StatTile
                      icon={<Zap size={15} />}
                      label="Actively exploring"
                      value={stats.activelyExploring}
                      accent="amber"
                    />
                    <StatTile
                      icon={<BadgeCheck size={15} />}
                      label="Verified"
                      value={stats.verified}
                      accent="sky"
                    />
                  </>
                ) : (
                  <StatTile
                    icon={<BadgeCheck size={15} />}
                    label="Active"
                    value={stats.verified}
                    accent="emerald"
                  />
                )}
              </div>

              {isCandidate && (
                <>
                  <div className="mt-4">
                    <div className="mb-1 flex items-center justify-between text-[11px] font-bold text-slate-500">
                      <span>Seniority bar</span>
                      <span>
                        {stats.seniorCandidates.toLocaleString()} of {stats.total.toLocaleString()} clear 10
                        years
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500"
                        style={{
                          width: `${stats.total > 0 ? Math.round((stats.seniorCandidates / stats.total) * 100) : 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <MiniDist
                      title="Role level"
                      values={stats.byRoleLevel}
                      activeValue={applied.roleLevel}
                      onPick={(v) => applyFilters({ ...applied, field: '', value: '', roleLevel: v })}
                      accent="from-violet-500 to-fuchsia-500"
                    />
                    <MiniDist
                      title="Experience"
                      values={stats.byExperience}
                      onPick={(v) => {
                        const bucket = parseExperienceBucket(v);
                        applyFilters({
                          ...applied,
                          field: '',
                          value: '',
                          minExperience: bucket.min,
                          maxExperience: bucket.max,
                        });
                      }}
                      accent="from-cyan-500 to-teal-500"
                    />
                    <MiniDist
                      title="Top cities"
                      values={stats.byCity}
                      activeValue={applied.city}
                      onPick={(v) => applyFilters({ ...applied, field: '', value: '', city: v })}
                      accent="from-sky-500 to-cyan-500"
                    />
                    <MiniDist
                      title="Domain"
                      values={stats.byDomain}
                      activeValue={applied.domain}
                      onPick={(v) => applyFilters({ ...applied, field: '', value: '', domain: v })}
                      accent="from-emerald-500 to-teal-500"
                    />
                  </div>
                </>
              )}
            </section>

            {/* Talent Insights */}
            <TalentInsights
              analytics={analytics}
              loading={analyticsLoading}
              activeField={applied.field}
              activeValue={applied.value}
              activeMin={applied.minExperience}
              activeMax={applied.maxExperience}
              onPick={applyAnalyticsFilter}
            />

            {/* Directory table */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                <div className="flex items-center gap-2">
                  <BarChart3 size={16} className="text-indigo-600" />
                  <h2 className="text-sm font-black text-slate-800">
                    {isCandidate ? 'A glimpse of the pool' : 'Recruiter preview'}
                  </h2>
                  <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-black text-indigo-600">
                    {total.toLocaleString()} {isCandidate ? 'candidates' : 'recruiters'}
                  </span>
                </div>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  <Lock size={12} /> Identities hidden
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-gradient-to-r from-indigo-50 via-white to-violet-50 text-[11px] uppercase tracking-wider text-slate-400">
                      <th className="px-4 py-3">Name</th>
                      {isCandidate ? (
                        <>
                          <th className="px-4 py-3">City</th>
                          <th className="px-4 py-3">Role</th>
                          <th className="px-4 py-3">Experience</th>
                          <th className="px-4 py-3">Availability</th>
                        </>
                      ) : (
                        <>
                          <th className="px-4 py-3">Designation</th>
                          <th className="px-4 py-3">Company</th>
                          <th className="px-4 py-3">Status</th>
                        </>
                      )}
                      <th className="px-4 py-3">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-10 text-center">
                          <Loader2 className="mx-auto animate-spin text-slate-300" size={20} />
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-10 text-center text-sm font-semibold text-slate-400">
                          No profiles match the current filters.
                        </td>
                      </tr>
                    ) : (
                      rows.map((row) =>
                        isCandidate ? (
                          <CandidateTableRow key={row.id} row={row as CandidateDataRow} />
                        ) : (
                          <RecruiterTableRow key={row.id} row={row as RecruiterDataRow} />
                        )
                      )
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3">
                <button
                  disabled={page <= 0}
                  onClick={() => gotoPage(Math.max(0, page - 1))}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 transition disabled:opacity-40"
                >
                  <ChevronLeft size={14} /> Prev
                </button>
                <span className="text-xs font-semibold text-slate-400">
                  Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
                </span>
                <button
                  disabled={page + 1 >= totalPages}
                  onClick={() => gotoPage(page + 1)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 transition disabled:opacity-40"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col items-center justify-between gap-4 rounded-2xl bg-gradient-to-r from-indigo-600 to-violet-600 p-6 text-white md:flex-row">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15">
                  <Rocket size={20} />
                </span>
                <div>
                  <h3 className="text-base font-black">
                    {stats.total.toLocaleString()} {isCandidate ? 'senior leaders' : 'recruiters'} are one
                    click away
                  </h3>
                  <p className="text-sm font-medium text-white/80">
                    Create your free account to unlock full profiles, contact and shortlist.
                  </p>
                </div>
              </div>
              <button
                onClick={submitOnboard}
                className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-xs font-black uppercase tracking-wider text-indigo-700 transition hover:bg-indigo-50"
              >
                <Send size={15} /> Register &amp; connect
              </button>
            </div>

            <p className="mt-8 text-center text-[11px] font-semibold text-slate-400">
              Shared securely via Nexgent · Link status: {status}
            </p>
        </main>
      </div>
    </div>
  );
};

/* ==========================================================================
   Row renderers (identities masked — no emails on the public page)
   ========================================================================== */

const CandidateTableRow = ({ row }: { row: CandidateDataRow }) => (
  <tr className="border-b border-slate-50 hover:bg-slate-50/70">
    <td className="px-4 py-3">
      <p className="font-bold text-slate-800">{maskName(row.fullName)}</p>
    </td>
    <td className="px-4 py-3 text-slate-600">
      <span className="inline-flex items-center gap-1.5">
        <MapPin size={12} className="text-slate-300" />
        {row.city ?? '—'}
      </span>
    </td>
    <td className="px-4 py-3 text-slate-600">
      <span className="inline-flex items-center gap-1.5">
        <Briefcase size={12} className="text-slate-300" />
        {row.currentRole ?? '—'}
      </span>
      {row.currentCompany && (
        <p className="text-[11px] font-semibold text-slate-400">{row.currentCompany}</p>
      )}
    </td>
    <td className="px-4 py-3 text-slate-600">
      {row.totalYearsOfExperience != null ? `${row.totalYearsOfExperience} yrs` : '—'}
    </td>
    <td className="px-4 py-3">
      {row.availabilityStatus ? (
        <span className="rounded-md bg-emerald-50 px-2 py-1 text-[10px] font-black uppercase text-emerald-600">
          {row.availabilityStatus}
        </span>
      ) : (
        '—'
      )}
    </td>
    <td className="px-4 py-3 text-[11px] font-semibold text-slate-400">{formatDate(row.createdAt)}</td>
  </tr>
);

const RecruiterTableRow = ({ row }: { row: RecruiterDataRow }) => (
  <tr className="border-b border-slate-50 hover:bg-slate-50/70">
    <td className="px-4 py-3">
      <p className="font-bold text-slate-800">{maskName(row.fullName)}</p>
    </td>
    <td className="px-4 py-3 text-slate-600">
      <span className="inline-flex items-center gap-1.5">
        <Briefcase size={12} className="text-slate-300" />
        {row.designation ?? '—'}
      </span>
      {row.companyAdmin && (
        <span className="ml-2 rounded-md bg-indigo-50 px-1.5 py-0.5 text-[9px] font-black uppercase text-indigo-600">
          Admin
        </span>
      )}
    </td>
    <td className="px-4 py-3 text-slate-600">
      <span className="inline-flex items-center gap-1.5">
        <Building2 size={12} className="text-slate-300" />
        {row.companyId ? row.companyId.slice(0, 8) : '—'}
      </span>
    </td>
    <td className="px-4 py-3">
      <span
        className={`rounded-md px-2 py-1 text-[10px] font-black uppercase ${
          row.active ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'
        }`}
      >
        {row.active ? 'Active' : 'Inactive'}
      </span>
    </td>
    <td className="px-4 py-3 text-[11px] font-semibold text-slate-400">{formatDate(row.createdAt)}</td>
  </tr>
);

/* ==========================================================================
   Small stat / distribution widgets (light theme)
   ========================================================================== */

const StatTile = ({
  icon,
  label,
  value,
  accent = 'indigo',
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  accent?: 'indigo' | 'emerald' | 'amber' | 'sky' | 'violet' | 'rose' | 'fuchsia' | 'cyan' | 'teal';
}) => {
  const ring: Record<string, string> = {
    indigo: 'border-indigo-200 bg-indigo-50 text-indigo-600',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-600',
    amber: 'border-amber-200 bg-amber-50 text-amber-600',
    sky: 'border-sky-200 bg-sky-50 text-sky-600',
    violet: 'border-violet-200 bg-violet-50 text-violet-600',
    rose: 'border-rose-200 bg-rose-50 text-rose-600',
    fuchsia: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-600',
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-600',
    teal: 'border-teal-200 bg-teal-50 text-teal-600',
  };
  const bar: Record<string, string> = {
    indigo: 'from-indigo-500 to-violet-500',
    emerald: 'from-emerald-500 to-teal-500',
    amber: 'from-amber-500 to-orange-500',
    sky: 'from-sky-500 to-cyan-500',
    violet: 'from-violet-500 to-fuchsia-500',
    rose: 'from-rose-500 to-pink-500',
    fuchsia: 'from-fuchsia-500 to-pink-500',
    cyan: 'from-cyan-500 to-teal-500',
    teal: 'from-teal-500 to-cyan-500',
  };
  return (
    <div className="overflow-hidden rounded-xl border border-slate-100 bg-slate-50">
      <div className={`h-1 w-full bg-gradient-to-r ${bar[accent]}`} />
      <div className="p-3">
        <span className={`mb-2 inline-flex h-8 w-8 items-center justify-center rounded-lg border ${ring[accent]}`}>
          {icon}
        </span>
        <p className="text-2xl font-black text-slate-800">{value.toLocaleString()}</p>
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      </div>
    </div>
  );
};

const MiniDist = ({
  title,
  values,
  activeValue,
  onPick,
  accent = 'from-indigo-500 to-violet-500',
}: {
  title: string;
  values?: FacetCount[];
  activeValue?: string;
  onPick?: (value: string) => void;
  accent?: string;
}) => {
  const list = values ?? [];
  const max = list.reduce((m, v) => Math.max(m, v.count), 0) || 1;
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
      <p className="mb-2 text-[11px] font-black uppercase tracking-wider text-slate-500">{title}</p>
      {list.length === 0 ? (
        <p className="text-[11px] font-semibold text-slate-400">No data</p>
      ) : (
        <div className="space-y-2">
          {list.slice(0, 5).map((v) => {
            const active = activeValue === v.value;
            return (
              <button
                key={v.value}
                type="button"
                disabled={!onPick}
                onClick={() => onPick?.(v.value)}
                className={`block w-full text-left ${onPick ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <div className="mb-0.5 flex items-center justify-between text-[11px]">
                  <span className={`truncate pr-2 font-semibold ${active ? 'text-indigo-600' : 'text-slate-500'}`}>
                    {v.value}
                  </span>
                  <span className="font-black text-slate-700">{v.count.toLocaleString()}</span>
                </div>
                <div className="h-1 w-full overflow-hidden rounded-full bg-slate-200">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r ${accent}`}
                    style={{ width: `${Math.max(6, Math.round((v.count / max) * 100))}%` }}
                  />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * "Talent Insights": every tracked attribute rendered as a value/count distribution, grouped by
 * category — identical catalogue to the admin console. Clickable bars drill the directory via the
 * generic field/value filter (merged server-side into the share's stored filters).
 */
const TalentInsights = ({
  analytics,
  loading,
  activeField,
  activeValue,
  activeMin,
  activeMax,
  onPick,
}: {
  analytics: TalentAnalytics | null;
  loading: boolean;
  activeField: string;
  activeValue: string;
  activeMin: string;
  activeMax: string;
  onPick: (section: AnalyticsSection, value: string) => void;
}) => {
  const grouped = useMemo(() => {
    const map = new Map<string, AnalyticsSection[]>();
    (analytics?.sections ?? []).forEach((s) => {
      const arr = map.get(s.category) ?? [];
      arr.push(s);
      map.set(s.category, arr);
    });
    return Array.from(map.entries())
      .map(([category, sections]) => ({ category, sections }))
      .sort((a, b) => {
        const ia = CATEGORY_ORDER.indexOf(a.category);
        const ib = CATEGORY_ORDER.indexOf(b.category);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      });
  }, [analytics]);

  const isActive = (section: AnalyticsSection, value: string): boolean => {
    if (section.key === 'experience') {
      const bucket = parseExperienceBucket(value);
      return (
        (bucket.min !== '' || bucket.max !== '') && bucket.min === activeMin && bucket.max === activeMax
      );
    }
    return !!section.filterField && section.filterField === activeField && activeValue === value;
  };

  return (
    <section className="rounded-2xl border border-t-4 border-slate-200 border-t-violet-400 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-400">
        <BarChart3 size={14} className="text-violet-500" /> Talent Insights
        <span className="ml-auto text-[10px] font-bold normal-case text-slate-400">
          {analytics ? `${analytics.total.toLocaleString()} in scope · every tracked field` : 'scoped to current filters'}
        </span>
      </div>

      {loading && !analytics ? (
        <div className="py-8 text-center">
          <Loader2 className="mx-auto animate-spin text-slate-300" size={20} />
        </div>
      ) : grouped.length === 0 ? (
        <p className="text-[11px] font-semibold text-slate-400">No analytics available.</p>
      ) : (
        <div className="space-y-5">
          {grouped.map(({ category, sections }, ci) => (
            <div key={category}>
              <p
                className={`mb-2 text-[10px] font-black uppercase tracking-[0.18em] ${
                  CATEGORY_COLORS[ci % CATEGORY_COLORS.length]
                }`}
              >
                {category}
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {sections.map((section) => {
                  const list = section.values.slice(0, 6);
                  const max = section.values.reduce((m, v) => Math.max(m, v.count), 0) || 1;
                  const clickableFor = (value: string) =>
                    !UNKNOWN_BUCKETS.has(value) && (section.key === 'experience' || !!section.filterField);
                  return (
                    <div
                      key={section.key}
                      className={`rounded-xl border border-l-4 border-slate-100 bg-slate-50 p-3 ${
                        CATEGORY_BORDERS[ci % CATEGORY_BORDERS.length]
                      }`}
                    >
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="truncate text-[11px] font-black uppercase tracking-wider text-slate-500">
                          {section.label}
                        </p>
                        <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-black text-slate-500">
                          {section.total.toLocaleString()}
                        </span>
                      </div>
                      {list.length === 0 ? (
                        <p className="text-[11px] font-semibold text-slate-400">No data</p>
                      ) : (
                        <div className="space-y-1.5">
                          {list.map((v) => {
                            const active = isActive(section, v.value);
                            const bar = (
                              <>
                                <div className="mb-0.5 flex items-center justify-between gap-2 text-[11px]">
                                  <span
                                    className={`truncate pr-2 font-semibold ${active ? 'text-indigo-600' : 'text-slate-500'}`}
                                  >
                                    {v.value}
                                  </span>
                                  <span className="shrink-0 font-black text-slate-700">
                                    {v.count.toLocaleString()}
                                  </span>
                                </div>
                                <div className="h-1 w-full overflow-hidden rounded-full bg-slate-200">
                                  <div
                                    className={`h-full rounded-full ${
                                      active
                                        ? 'bg-indigo-500'
                                        : 'bg-gradient-to-r from-indigo-500 to-violet-500'
                                    }`}
                                    style={{ width: `${Math.max(5, Math.round((v.count / max) * 100))}%` }}
                                  />
                                </div>
                              </>
                            );
                            return clickableFor(v.value) ? (
                              <button
                                key={v.value}
                                type="button"
                                onClick={() => onPick(section, v.value)}
                                className="block w-full cursor-pointer text-left"
                                title={`Filter by ${section.label}: ${v.value}`}
                              >
                                {bar}
                              </button>
                            ) : (
                              <div key={v.value} className="cursor-default">
                                {bar}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default SharedDirectoryPage;
